import { useState } from "react"

import { SignUp, useAuth } from "@clerk/react"
import { Navigate } from "@tanstack/react-router"

import { AuthShell } from "@/features/auth/auth-shell"
import { Button } from "@/components/ui/button"

interface SignUpPageProps {
  redirectUrl?: string
}

function resolveSignUpFallbackRedirectUrl(redirectUrl: string | undefined) {
  return redirectUrl ?? "/account"
}

export function SignUpPage({ redirectUrl }: SignUpPageProps) {
  const { isSignedIn, isLoaded } = useAuth()
  const [ageGate, setAgeGate] = useState<"adult_18_plus" | "under_18" | null>(null)
  const fallbackRedirectUrl = resolveSignUpFallbackRedirectUrl(redirectUrl)

  if (isLoaded && isSignedIn) {
    return <Navigate to={fallbackRedirectUrl} replace />
  }

  if (ageGate === "under_18") {
    return <Navigate to="/register" replace />
  }

  return (
    <AuthShell
      title="Create your SafetyQR account"
      description="Adult self-serve sign-up is available here. Under-18 registrations must continue through the guardian approval flow."
    >
      {ageGate === "adult_18_plus" ? (
        <SignUp
          fallbackRedirectUrl={fallbackRedirectUrl}
          path="/sign-up"
          routing="path"
          signInUrl="/sign-in"
        />
      ) : (
        <div className="space-y-4 rounded-3xl border border-border/70 bg-background/80 p-6">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-foreground">
              Confirm the registration path
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Adults can continue with self-serve account creation. Under-18 users
              cannot create their own SafetyQR account from this page and must use
              the guardian approval registration flow instead.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Button onClick={() => setAgeGate("adult_18_plus")} type="button">
              I am 18 or older
            </Button>
            <Button
              onClick={() => setAgeGate("under_18")}
              type="button"
              variant="outline"
            >
              I am under 18
            </Button>
          </div>
        </div>
      )}
    </AuthShell>
  )
}
